import { useParams } from "react-router";
import { Trans, useTranslation } from "@coworker/locales";
import FullScreenPopup from "@coworker/apprestructured/src/layout/components/FullScreenPopup/FullScreenPopup";
import { useTasksForProduct } from "../hooks/useTasksForProduct";
import { useItemInfo } from "@coworker/apprestructured/src/shared/hooks/item/useItemInfo";
import { TaskCard as TaskCardV1 } from "@coworker/apprestructured/src/tasks/components/TaskCard/TaskCard";
import { TaskCardSkeleton } from "@coworker/apprestructured/src/tasks/components/TaskCard/TaskCardSkeleton";
import { TaskType } from "@coworker/apprestructured/src/tasks/enums/taskTypes";
import { FullTask } from "@coworker/apprestructured/src/TasksOverview/types/FullTask";
import { ModalContext } from "@coworker/apprestructured/src/layout/context/ModalContextProvider";
import { ModalType } from "@coworker/apprestructured/src/layout/context/modalType";
import { useContext, useCallback } from "react";
import Linkbar from "@coworker/apprestructured/src/shared/components/Linkbar/Linkbar";
import useSearchParamState from "@coworker/apprestructured/src/shared/hooks/useSearchParamState";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import TaskCardV2 from "@coworker/apprestructured/src/task-management/components/TaskCard/TaskCard";
import ListableTaskCardWrapper from "@coworker/apprestructured/src/task-management/components/TaskCard/wrappers/NormalTaskWrapper/ListableTaskCardWrapper";
import { Task } from "@coworker/apprestructured/src/task-management/types/Task";

type RelatedTask =
  | { version: "v1"; task: FullTask }
  | { version: "v2"; task: Task };

function createdAtDescending(first: RelatedTask, second: RelatedTask) {
  return (
    new Date(second.task.created_at).getTime() -
    new Date(first.task.created_at).getTime()
  );
}

export const RelatedTasks = () => {
  const { id } = useParams();
  const tasks = useTasksForProduct(id);
  const { data: basicItem, isLoading: isItemInfoLoading } = useItemInfo(
    id ?? ""
  );
  const { toggleModal } = useContext(ModalContext);
  const { t } = useTranslation();
  const [, setSearchParams] = useSearchParamState();

  const onShowTaskDetails = useCallback(
    (taskId: string, taskType: TaskType) => {
      toggleModal({
        type: ModalType.TaskDetail,
        payload: { taskId, taskType },
      });
    },
    [toggleModal]
  );

  const onShowV2TaskDetails = useCallback(
    (taskId: string) => {
      setSearchParams((params) => {
        params.set("taskViewer", taskId);
      });
    },
    [setSearchParams]
  );

  if (tasks.loading || isItemInfoLoading)
    return (
      <>
        <TaskCardSkeleton />
        <TaskCardSkeleton />
        <TaskCardSkeleton />
        <TaskCardSkeleton />
      </>
    );

  const v1Tasks = tasks.v1Tasks ?? tasks.list;
  const v2Tasks = tasks.v2Tasks ?? [];
  const relatedTasks: RelatedTask[] = [
    ...v1Tasks.map((task: FullTask) => ({ version: "v1" as const, task })),
    ...v2Tasks.map((task: Task) => ({ version: "v2" as const, task })),
  ].sort(createdAtDescending);

  return (
    <FullScreenPopup
      appBarConfig={{
        title: <Trans>relatedTasksString</Trans>,
        subtitle: (
          <>
            {!!id && (
              <>
                {basicItem?.noFormatted}
                {!!basicItem?.name && (
                  <>
                    {" • "}
                    {basicItem?.name}
                  </>
                )}
              </>
            )}
          </>
        ),
      }}
      linkbar={
        <Linkbar
          items={[
            {
              label: t("TasksString"),
              to: "/tasks",
            },
          ]}
          currentLabel={t("relatedTasksString")}
          currentAsH1
        />
      }
      noPadding
    >
      {relatedTasks.length > 0 && (
        <FixaListView id="related-tasks">
          {relatedTasks.map(({ version, task }) =>
            version === "v2" ? (
              <ListableTaskCardWrapper
                key={`v2-${task.id}`}
                onSelect={() => onShowV2TaskDetails(task.id)}
              >
                <TaskCardV2 task={task} />
              </ListableTaskCardWrapper>
            ) : (
              <TaskCardV1
                key={`v1-${task.id}`}
                task={task}
                item={basicItem!}
                onShowTaskDetails={onShowTaskDetails}
              />
            )
          )}
        </FixaListView>
      )}
    </FullScreenPopup>
  );
};
